// Write hooks for findings.
//
//   - `useReplaceFindings` — wraps the reconcile wrapper for a transaction's
//     findings (RTM Workstream I: keep/update/close/insert, never delete-then-
//     reinsert); invalidate-on-success (a buyer fix is kept as a closed row, so the
//     effective compliance / register must refetch).
//
// Errors are NOT swallowed: onError surfaces a toast AND the hook keeps
// `error`/`isError` for the caller.
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { invalidateRegister } from '../lib/queryKeys';
import { useToast } from '../components/ui/Toast';
import { reconcileFindingsForTx, type FindingInsertInput } from '../lib/dbWrite';

interface ReplaceFindingsArgs {
  referenceNo: string;
  findings: FindingInsertInput[];
}

export function useReplaceFindings() {
  const { show } = useToast();
  return useMutation({
    mutationFn: ({ referenceNo, findings }: ReplaceFindingsArgs) =>
      reconcileFindingsForTx(referenceNo, findings),
    onSuccess: () => {
      // Findings feed the transaction's effective compliance, the working-paper
      // view and the reviewer-scoped register — invalidate the whole surface.
      invalidateRegister(queryClient);
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to save the findings.', 'error');
    },
  });
}
